import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useUpdateGameV2 } from "@seabrookstudios/pitch2table";
import { formatDuration } from "./formatters";
import { FullScreenDialog } from "../core/FullScreenDialog";
import Box from "@mui/material/Box";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import ButtonGroup from "@mui/material/ButtonGroup";
import CardHeader from "@mui/material/CardHeader";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").Game} props.game
 */
export const EditDurationControl = ({ game }) => {
  const onUpdateSuccess = () => {
    enqueueSnackbar("Saved", { variant: "success" });
  };

  const onUpdateFailure = () => {
    enqueueSnackbar("Update failed", { variant: "error" });
  };

  const updateGame = useUpdateGameV2(game.id, onUpdateSuccess, onUpdateFailure);

  /**
   * @param {string} duration
   * @param {number} minDuration
   * @param {number} maxDuration
   * @param {import('@seabrookstudios/pitch2table-core').SupportedDurationTypes} durationType
   */
  const onSave = async (duration, minDuration, maxDuration, durationType) => {
    await updateGame.mutateAsync({
      duration,
      minDuration,
      maxDuration,
      durationType,
      sizzle: undefined,
      bgg: undefined,
      personalGoal: undefined,
      customAuthors: undefined,
      gameType: undefined,
      referenceId: undefined,
      tags: undefined,
      feedbackEnabled: undefined,
    });
  };

  const [newDuration, setDuration] = useState(game.duration || "");
  const [newMin, setMin] = useState(`${game.minDuration}`);
  const [newMax, setMax] = useState(`${game.maxDuration}`);
  const [newType, setType] = useState(game.durationType || "other");
  const [disabled, setDisabled] = useState(true);

  /**
   * Save the value with some behaviour. All of this is tricky as parseInt will never throw
   * - swap min and max if that makes more sense
   * - switch to single if min and max are the same value
   * - other values are accepted as is
   */
  const onSaveHandler = () => {
    const nMin = Number.parseInt(newMin, 10);
    const nMax = Number.parseInt(newMax, 10);

    const finalMin = nMin < nMax ? nMin : nMax;
    const finalMax = nMin > nMax ? nMin : nMax;
    const postSwapped = game.durationType === "range" ? `${finalMin}-${finalMax} minutes` : newDuration;

    const finalType = finalMin === finalMax && game.durationType !== "per-player" ? "fixed" : newType;
    const finalPlayerCount = finalType === "fixed" ? `${finalMin} minutes` : postSwapped;

    onSave(finalPlayerCount, finalMin, finalMax, finalType);
  };

  useEffect(() => {
    if (newType === "other") {
      setDisabled(newDuration.length === 0);
    }
    if (newType === "fixed") {
      const value = Number.parseInt(newMin, 10);
      setDisabled(Number.isNaN(value) || newMin.length === 0);
    }
    if (newType === "per-player") {
      const value = Number.parseInt(newMin, 10);
      setDisabled(Number.isNaN(value) || newMin.length === 0);
    }
    if (newType === "range") {
      const v1 = Number.parseInt(newMin, 10);
      const v2 = Number.parseInt(newMax, 10);
      setDisabled(Number.isNaN(v1) || newMin.length === 0 || Number.isNaN(v2) || newMax.length === 0);
    }
  }, [newDuration, newMax, newMin, newType]);

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const setOtherFromEvent = (e) => {
    const v = e.target.value;

    setOther(v);
  };

  /**
   * @param {string} v
   */
  const setOther = (v) => {
    setDuration(v);
  };

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const setFixedFromEvent = (e) => {
    const v = e.target.value;

    setFixed(v);
  };

  /**
   * @param {string} v
   */
  const setFixed = (v) => {
    if (v.length === 0) {
      setDuration("");
      setMin("");
      setMax("");
      return;
    }

    const value = Number.parseInt(v, 10);
    if (Number.isNaN(value) || value === 0) {
      setMin("");
      setMax("");
      setDuration("");
      return;
    }

    setDuration(`${v} minutes`);
    setMin(`${value}`);
    setMax(`${value}`);
  };

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const setPerPlayerFromEvent = (e) => {
    const v = e.target.value;

    setPerPlayer(v);
  };

  /**
   * @param {string} v
   */
  const setPerPlayer = (v) => {
    if (v.length === 0) {
      setDuration("");
      setMin("");
      setMax("");
      return;
    }

    const value = Number.parseInt(v, 10);
    if (Number.isNaN(value) || value === 0) {
      setMin("");
      setMax("");
      setDuration("");
      return;
    }

    setDuration(`${v} minutes / player`);
    setMin(`${value}`);
    setMax(`${value}`);
  };

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const setRangeLowerFromEvent = (e) => {
    const v = e.target.value;

    setRangeLower(v);
  };

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const setRangeUpperFromEvent = (e) => {
    const v = e.target.value;

    setRangeUpper(v);
  };

  /**
   * @param {string} v
   */
  const setRangeLower = (v) => {
    if (v.length === 0) {
      setDuration("");
      setMin("");
      return;
    }

    const value = Number.parseInt(v, 10);
    if (Number.isNaN(value) || value === 0) {
      setDuration("");
      setMin("");
      return;
    }

    setDuration(`${value}-${newMax} minutes`);
    setMin(`${value}`);
  };

  /**
   * @param {string} v
   */
  const setRangeUpper = (v) => {
    if (v.length === 0) {
      setDuration("");
      setMax("");
      return;
    }

    const value = Number.parseInt(v, 10);
    if (Number.isNaN(value)) {
      setDuration("");
      setMax("");
      return;
    }

    setDuration(`${newMin}-${value} minutes`);
    setMax(`${value}`);
  };

  /**
   * @param {number} n
   */
  const setQuick = (n) => {
    const onPressHandler = () => {
      if (newType === "per-player") {
        setPerPlayer(`${n}`);
        return;
      }

      setType("fixed");
      setFixed(`${n}`);
    };

    return onPressHandler;
  };

  /**
   * @param {number} n1
   * @param {number} n2
   */
  const setRange = (n1, n2) => {
    const onPressHandler = () => {
      setType("range");
      setMin(`${n1}`);
      setMax(`${n2}`);
      setDuration(`${n1}-${n2} minutes`);
    };

    return onPressHandler;
  };

  return (
    <FullScreenDialog
      title={`Edit duration`}
      actionText="SAVE"
      onSaveHandler={onSaveHandler}
      isBusy={updateGame.isPending}
      CustomControl={
        <ListItemText primary={"Duration"} secondary={formatDuration(game.duration) || "Click to edit."} />
      }
      canSave={disabled}
    >
      <Box padding={2} display="flex" justifyContent="center">
        <ButtonGroup variant="outlined" disableElevation style={{ width: "80%" }}>
          <Button
            variant={newType === "fixed" ? "contained" : "outlined"}
            style={{ width: "100%" }}
            onClick={() => setType("fixed")}
          >
            FIXED
          </Button>
          <Button
            variant={newType === "range" ? "contained" : "outlined"}
            style={{ width: "100%" }}
            onClick={() => setType("range")}
          >
            RANGE
          </Button>
          <Button
            variant={newType === "per-player" ? "contained" : "outlined"}
            style={{ width: "100%" }}
            onClick={() => setType("per-player")}
          >
            PER PLAYER
          </Button>
          <Button
            variant={newType === "other" ? "contained" : "outlined"}
            style={{ width: "100%" }}
            onClick={() => setType("other")}
          >
            OTHER
          </Button>
        </ButtonGroup>
      </Box>
      <Box p={1}>
        {newType === "other" && (
          <Stack direction="row">
            <TextField
              style={{ width: "100%" }}
              value={newDuration}
              label="Non-standard duration"
              autoFocus
              onChange={setOtherFromEvent}
              helperText="How long does a game usually go for e.g. 4 hours"
            />
          </Stack>
        )}
        {newType === "fixed" && (
          <Stack direction="row">
            <TextField
              style={{ width: "100%" }}
              value={newMin}
              label="Fixed Duration"
              autoFocus
              onChange={setFixedFromEvent}
              type="number"
              helperText="How long does a game usually go for e.g. 20 minutes"
            />
          </Stack>
        )}
        {newType === "per-player" && (
          <Stack direction="row">
            <TextField
              style={{ width: "100%" }}
              value={newMin}
              label="Per-Player Duration"
              autoFocus
              onChange={setPerPlayerFromEvent}
              type="number"
              helperText="How long does a game usually go for e.g. 15 minutes per-player"
            />
          </Stack>
        )}
        {newType === "range" && (
          <Stack direction="row">
            <TextField
              value={newMin}
              style={{ width: "100%" }}
              label="Minimum duration"
              autoFocus
              onChange={setRangeLowerFromEvent}
              type="number"
              helperText="How long does a game usually go for e.g. 30-60 minutes"
            />
            <Box paddingLeft={1} />
            <TextField
              value={newMax}
              style={{ width: "100%" }}
              label="Maximum duration"
              autoFocus
              onChange={setRangeUpperFromEvent}
              type="number"
            />
          </Stack>
        )}
      </Box>
      <Box padding={1}>
        <CardHeader subheader="Quick Options" />
        <Grid container spacing={2}>
          <Grid item size={{ xs: 6, sm: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(15)}>
              15 minutes
            </Button>
          </Grid>
          <Grid item size={{ xs: 6, sm: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setRange(15, 20)}>
              15 - 20 minutes
            </Button>
          </Grid>
          <Grid item size={{ xs: 6, sm: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(20)}>
              20 minutes
            </Button>
          </Grid>
          <Grid item size={{ xs: 6, sm: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setRange(20, 30)}>
              20 - 30 minutes
            </Button>
          </Grid>
          <Grid item size={{ xs: 6, sm: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(30)}>
              30 minutes
            </Button>
          </Grid>
          <Grid item size={{ xs: 6, sm: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setRange(30, 45)}>
              30 - 45 minutes
            </Button>
          </Grid>
          <Grid item size={{ xs: 6, sm: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(60)}>
              60 minutes
            </Button>
          </Grid>
          <Grid item size={{ xs: 6, sm: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setRange(30, 60)}>
              30 - 60 minutes
            </Button>
          </Grid>
          <Grid item size={{ xs: 6, sm: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(90)}>
              90 minutes
            </Button>
          </Grid>
          <Grid item size={{ xs: 6, sm: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setRange(60, 120)}>
              60 - 120 minutes
            </Button>
          </Grid>
        </Grid>
      </Box>
    </FullScreenDialog>
  );
};
